/**
 * Admin V5 — Visual Test Builder (Digital SAT · Phase 2, Reusable Exam Engine).
 *
 * Backend contract (all under /api/exam/v2):
 *   GET    /tests?exam_type=&status=            list tests (all statuses when admin+status)
 *   POST   /tests                                create draft
 *   GET    /tests/{id}                           fetch one
 *   PATCH  /tests/{id}                           update DRAFT only (published are immutable)
 *   POST   /tests/{id}/publish                   publish draft
 *   POST   /tests/{id}/archive                   archive
 *   POST   /tests/{id}/duplicate                 clone as new draft
 *   POST   /tests/{id}/new-version               archive published parent + create v2 draft
 *   POST   /tests/estimate                       {question_ids} → duration + difficulty mix
 *   GET    /tests/{id}/analytics                 attempts, avg score, avg completion
 *   GET    /questions?filters                    filtered question search
 *   POST   /attempts/start  {test_id, preview:true, mode:'timed'|'untimed'}  admin-only preview
 *
 * UX
 *   - Left panel: filter form (search + domain + skill + subskill + module +
 *     difficulty + question_type + status + tags).
 *   - Right panel: selected questions with drag-reorder, remove and estimated
 *     duration/difficulty counters + running totals.
 *   - 4-step wizard: (1) Type & Info · (2) Modules & Timing · (3) Question
 *     Selection (split view) · (4) Review + Preview + Publish.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, Search, Trash2, ChevronUp, ChevronDown, Eye, Send, Save,
  Copy, Archive, GitBranch, RefreshCcw, ArrowRight, ArrowLeft, X, Clock, ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const TEST_TYPES = [
  { code: 'topic', label: 'Topic Practice', desc: '1 short module drilling a domain/skill.' },
  { code: 'short_section', label: 'Short Section', desc: '~15 questions, timed 15–20 min.' },
  { code: 'full_section', label: 'Full Section', desc: 'Complete R&W or Math section (2 modules).' },
  { code: 'full_mock', label: 'Full Mock', desc: 'Both sections × 2 modules, adaptive.' },
];

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const QTYPES = ['mcq', 'spr'];

const emptyModule = (code = 'm1', section = 'math') => ({
  code,
  label: code.toUpperCase(),
  section,
  minutes: 20,
  question_count: 10,
  selection_mode: 'manual',
  question_ids: [],
  filters: { section, domain: '', skill: '', difficulty: '', question_type: '', tags: [] },
  calculator: false,
});

const defaultBlueprintFor = (test_type) => {
  const base = {
    exam_type: 'dsat',
    title: '',
    description: '',
    test_type,
    section: 'math',
    time_limit_minutes: 20,
    allow_untimed: true,
    tags: [],
    status: 'draft',
    modules: [],
  };
  if (test_type === 'full_mock') {
    base.section = null;
    base.modules = [
      { ...emptyModule('rw_m1', 'rw'), label: 'R&W Module 1', minutes: 32, question_count: 27 },
      { ...emptyModule('rw_m2', 'rw'), label: 'R&W Module 2', minutes: 32, question_count: 27 },
      { ...emptyModule('math_m1', 'math'), label: 'Math Module 1', minutes: 35, question_count: 22, calculator: true },
      { ...emptyModule('math_m2', 'math'), label: 'Math Module 2', minutes: 35, question_count: 22, calculator: true },
    ];
  } else if (test_type === 'full_section') {
    base.modules = [
      { ...emptyModule('m1', 'math'), minutes: 35, question_count: 22 },
      { ...emptyModule('m2', 'math'), minutes: 35, question_count: 22 },
    ];
  } else if (test_type === 'short_section') {
    base.modules = [{ ...emptyModule('m1', 'math'), minutes: 20, question_count: 15 }];
  } else {
    base.modules = [{ ...emptyModule('m1', 'math'), minutes: 15, question_count: 10 }];
  }
  return base;
};

export default function AdminTestBuilderPanel() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTest, setEditingTest] = useState(null); // full test doc when editing
  const [statusFilter, setStatusFilter] = useState('draft');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all'
        ? `/api/exam/v2/tests?exam_type=dsat`
        : `/api/exam/v2/tests?exam_type=dsat&status=${statusFilter}`;
      const r = await ax.get(url);
      setTests(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const startNew = () => { setEditingTest(null); setShowBuilder(true); };
  const startEdit = async (test) => {
    try {
      const r = await ax.get(`/api/exam/v2/tests/${test.test_id}`);
      setEditingTest(r.data);
      setShowBuilder(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load test failed'); }
  };

  const duplicate = async (test) => {
    try {
      const r = await ax.post(`/api/exam/v2/tests/${test.test_id}/duplicate`);
      toast.success('Duplicated as draft');
      refresh();
      setEditingTest(r.data.test); setShowBuilder(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
  };

  const archive = async (test) => {
    if (!window.confirm(`Archive "${test.title}"? Historical attempts remain intact.`)) return;
    try {
      await ax.post(`/api/exam/v2/tests/${test.test_id}/archive`);
      toast.success('Archived');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Archive failed'); }
  };

  const newVersion = async (test) => {
    if (!window.confirm(`Create Version ${(test.version || 1) + 1}? The current published test will be archived — student attempts remain attached to it.`)) return;
    try {
      const r = await ax.post(`/api/exam/v2/tests/${test.test_id}/new-version`);
      toast.success(`Version ${r.data.test.version} draft created`);
      refresh();
      setEditingTest(r.data.test); setShowBuilder(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'New version failed'); }
  };

  if (showBuilder) {
    return (
      <TestBuilder
        existing={editingTest}
        onClose={(saved) => { setShowBuilder(false); setEditingTest(null); if (saved) refresh(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-test-builder-panel">
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div>
            <div className="text-lg font-bold text-gray-900">Visual Test Builder</div>
            <div className="text-xs text-gray-600 mt-0.5">
              Assemble Topic Practice · Short Section · Full Section · Full Mock — all powered by the Reusable Exam Engine.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              className="border rounded px-2 py-1.5 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="tb-status-filter"
            >
              <option value="draft">Drafts</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
              <option value="all">All</option>
            </select>
            <Button size="sm" variant="outline" onClick={refresh} data-testid="tb-refresh">
              <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={startNew} className="bg-red-700 hover:bg-red-800 text-white" data-testid="tb-new-test">
              <Plus className="h-4 w-4 mr-1" /> New Test
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
        ) : tests.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm" data-testid="tb-empty">
            No tests in this filter. Click <b>New Test</b> to build one.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Section</th>
                <th className="text-left px-3 py-2">Modules</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Ver.</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tests.map((t) => {
                const totalQ = (t.modules || []).reduce((sum, m) => sum + ((m.question_ids?.length) || (m.question_count || 0)), 0);
                return (
                  <tr key={t.test_id} className="border-t" data-testid={`tb-row-${t.test_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-900">{t.title}</div>
                      <div className="text-[10px] text-gray-500">{t.description || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-700 capitalize">{t.test_type?.replace('_', ' ')}</td>
                    <td className="px-3 py-2 text-gray-700 uppercase">{t.section || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{(t.modules || []).length} · {totalQ} q</td>
                    <td className="px-3 py-2">
                      <Badge className={
                        t.status === 'published' ? 'bg-emerald-100 text-emerald-800' :
                        t.status === 'archived' ? 'bg-gray-200 text-gray-700' :
                        'bg-amber-100 text-amber-800'
                      }>{t.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-gray-700">v{t.version || 1}</td>
                    <td className="px-3 py-2 text-gray-500 text-[11px]">
                      {t.updated_at ? new Date(t.updated_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {t.status === 'draft' && (
                        <Button size="sm" variant="outline" className="mr-1" onClick={() => startEdit(t)} data-testid={`tb-edit-${t.test_id}`}>Edit</Button>
                      )}
                      {t.status === 'published' && (
                        <>
                          <Button size="sm" variant="outline" className="mr-1" onClick={() => startEdit(t)} data-testid={`tb-view-${t.test_id}`}>View</Button>
                          <Button size="sm" variant="outline" className="mr-1" onClick={() => newVersion(t)} data-testid={`tb-newver-${t.test_id}`}>
                            <GitBranch className="h-3.5 w-3.5 mr-1" />v{(t.version || 1) + 1}
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => duplicate(t)} data-testid={`tb-dup-${t.test_id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      {t.status !== 'archived' && (
                        <Button size="sm" variant="outline" onClick={() => archive(t)} data-testid={`tb-archive-${t.test_id}`}>
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}


// ---------- Builder (wizard) ----------
function TestBuilder({ existing, onClose }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const isPublished = existing?.status === 'published';
  const readOnly = isPublished; // published are immutable

  const [blueprint, setBlueprint] = useState(() => {
    if (existing) {
      return {
        ...existing,
        modules: (existing.modules || []).map((m) => ({
          ...m,
          filters: m.filters || { section: m.section || '', domain: '', skill: '', difficulty: '', question_type: '', tags: [] },
          question_ids: m.question_ids || [],
        })),
      };
    }
    return defaultBlueprintFor('topic');
  });

  const [activeModuleIdx, setActiveModuleIdx] = useState(0);

  const setBP = (patch) => setBlueprint((p) => ({ ...p, ...patch }));
  const setModule = (idx, patch) => setBlueprint((p) => ({
    ...p,
    modules: p.modules.map((m, i) => i === idx ? { ...m, ...patch } : m),
  }));
  const setModuleFilter = (idx, patch) => setBlueprint((p) => ({
    ...p,
    modules: p.modules.map((m, i) => i === idx ? { ...m, filters: { ...(m.filters || {}), ...patch } } : m),
  }));

  const changeType = (test_type) => {
    if (readOnly) return;
    const fresh = defaultBlueprintFor(test_type);
    setBlueprint((p) => ({ ...fresh, title: p.title, description: p.description }));
  };

  const save = async () => {
    if (readOnly) { toast.error('Published tests are immutable. Use New Version.'); return; }
    if (!blueprint.title?.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const body = { ...blueprint, status: 'draft' };
      if (existing?.test_id) {
        await ax.patch(`/api/exam/v2/tests/${existing.test_id}`, body);
        toast.success('Draft saved');
      } else {
        const r = await ax.post('/api/exam/v2/tests', body);
        toast.success('Draft created');
        // move to editing mode so subsequent saves go to PATCH
        setBlueprint(r.data.test);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (readOnly) return;
    if (!blueprint.title?.trim()) { toast.error('Title is required'); return; }
    // Validate every module has at least 1 question or is random with count > 0
    const modules = blueprint.modules || [];
    for (const m of modules) {
      if (m.selection_mode === 'manual' && (m.question_ids || []).length === 0) {
        toast.error(`Module "${m.label || m.code}" has 0 manual questions.`); return;
      }
      if (m.selection_mode === 'random' && (!m.question_count || m.question_count < 1)) {
        toast.error(`Module "${m.label || m.code}" needs question_count ≥ 1.`); return;
      }
    }
    if (!window.confirm('Publish this test? Once published it becomes immutable. Edits will require a new version.')) return;
    setPublishing(true);
    try {
      // Save first as draft
      let testId = existing?.test_id || blueprint.test_id;
      if (!testId) {
        const r = await ax.post('/api/exam/v2/tests', { ...blueprint, status: 'draft' });
        testId = r.data.test.test_id;
      } else {
        await ax.patch(`/api/exam/v2/tests/${testId}`, { ...blueprint, status: 'draft' });
      }
      await ax.post(`/api/exam/v2/tests/${testId}/publish`);
      toast.success('Test published');
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
    finally { setPublishing(false); }
  };

  const previewAsStudent = async () => {
    if (!blueprint.title?.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      let testId = existing?.test_id || blueprint.test_id;
      if (!testId) {
        const r = await ax.post('/api/exam/v2/tests', { ...blueprint, status: 'draft' });
        testId = r.data.test.test_id;
        setBlueprint(r.data.test);
      } else if (!readOnly) {
        await ax.patch(`/api/exam/v2/tests/${testId}`, { ...blueprint, status: 'draft' });
      }
      const r2 = await ax.post('/api/exam/v2/attempts/start', { test_id: testId, mode: 'timed', preview: true });
      const attempt_id = r2.data.attempt_id;
      // Open in new tab so admin doesn't lose builder state
      window.open(`/dsat/attempt/${attempt_id}?preview=1`, '_blank');
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="test-builder">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-lg font-bold text-gray-900">
            {existing ? (readOnly ? 'View Test' : 'Edit Draft') : 'New Test'}
          </div>
          <div className="text-xs text-gray-600">
            {readOnly ? 'Published tests are immutable. Use "New version" to make edits.' :
              'Complete the four steps to publish this test.'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => onClose(false)} data-testid="tb-close">
            <X className="h-4 w-4 mr-1" /> Close
          </Button>
          {!readOnly && (
            <>
              <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="tb-save-draft">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save Draft
              </Button>
              <Button size="sm" variant="outline" onClick={previewAsStudent} disabled={saving} data-testid="tb-preview">
                <Eye className="h-4 w-4 mr-1" /> Preview as Student
              </Button>
              <Button size="sm" onClick={publish} disabled={publishing} className="bg-green-700 hover:bg-green-800 text-white" data-testid="tb-publish">
                {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Publish Test
              </Button>
            </>
          )}
        </div>
      </div>

      <StepStrip step={step} setStep={setStep} />

      {step === 1 && (
        <StepTypeAndInfo blueprint={blueprint} setBP={setBP} changeType={changeType} readOnly={readOnly} />
      )}
      {step === 2 && (
        <StepModulesTiming
          blueprint={blueprint} setBP={setBP} setModule={setModule} readOnly={readOnly}
          activeModuleIdx={activeModuleIdx} setActiveModuleIdx={setActiveModuleIdx}
        />
      )}
      {step === 3 && (
        <StepQuestionSelection
          blueprint={blueprint} setModule={setModule} setModuleFilter={setModuleFilter}
          activeModuleIdx={activeModuleIdx} setActiveModuleIdx={setActiveModuleIdx}
          readOnly={readOnly}
        />
      )}
      {step === 4 && (
        <StepReview blueprint={blueprint} existing={existing} />
      )}

      <div className="flex items-center justify-between pt-2">
        <Button size="sm" variant="outline" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="tb-prev-step">
          <ArrowLeft className="h-4 w-4 mr-1" /> Previous
        </Button>
        <div className="text-[11px] text-gray-500">Step {step} of 4</div>
        <Button size="sm" variant="outline" onClick={() => setStep((s) => Math.min(4, s + 1))} disabled={step === 4} data-testid="tb-next-step">
          Next <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function StepStrip({ step, setStep }) {
  const steps = ['Type & Info', 'Modules & Timing', 'Question Selection', 'Review'];
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="tb-stepper">
      {steps.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        return (
          <button
            key={label}
            onClick={() => setStep(n)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${active ? 'bg-red-700 text-white border-red-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            data-testid={`tb-step-${n}`}
          >
            {n}. {label}
          </button>
        );
      })}
    </div>
  );
}

function StepTypeAndInfo({ blueprint, setBP, changeType, readOnly }) {
  return (
    <Card className="p-4 space-y-4" data-testid="tb-step-info">
      <div>
        <Label className="text-xs">Test Type</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
          {TEST_TYPES.map((t) => {
            const active = blueprint.test_type === t.code;
            return (
              <button
                key={t.code}
                disabled={readOnly}
                onClick={() => changeType(t.code)}
                className={`text-left p-3 rounded border ${active ? 'border-red-700 bg-red-50' : 'border-gray-200 hover:border-gray-400'} disabled:opacity-60`}
                data-testid={`tb-type-${t.code}`}
              >
                <div className="text-sm font-bold text-gray-900">{t.label}</div>
                <div className="text-[11px] text-gray-600 mt-0.5">{t.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Title *</Label>
          <Input
            value={blueprint.title || ''}
            onChange={(e) => setBP({ title: e.target.value })}
            placeholder='e.g. "Selective Entry — Algebra Warmup"'
            data-testid="tb-title" disabled={readOnly}
          />
        </div>
        {blueprint.test_type !== 'full_mock' && (
          <div>
            <Label className="text-xs">Section</Label>
            <select
              className="border rounded px-2 py-1.5 text-sm w-full h-9"
              value={blueprint.section || 'math'}
              onChange={(e) => setBP({ section: e.target.value })}
              disabled={readOnly}
              data-testid="tb-section"
            >
              <option value="math">Math</option>
              <option value="rw">Reading &amp; Writing</option>
            </select>
          </div>
        )}
        <div className="md:col-span-2">
          <Label className="text-xs">Description</Label>
          <Textarea
            value={blueprint.description || ''}
            onChange={(e) => setBP({ description: e.target.value })}
            placeholder="Short description shown to students on the test intro screen."
            rows={2}
            disabled={readOnly}
            data-testid="tb-description"
          />
        </div>
        {blueprint.test_type !== 'full_mock' && (
          <div>
            <Label className="text-xs">Time limit (minutes)</Label>
            <Input
              type="number" min={1} max={300}
              value={blueprint.time_limit_minutes || 20}
              onChange={(e) => setBP({ time_limit_minutes: parseInt(e.target.value || '0', 10) })}
              disabled={readOnly}
              data-testid="tb-time-limit"
            />
          </div>
        )}
        <div className="flex items-end gap-3">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={!!blueprint.allow_untimed}
              onChange={(e) => setBP({ allow_untimed: e.target.checked })}
              disabled={readOnly}
              data-testid="tb-allow-untimed"
            />
            Allow untimed practice
          </label>
        </div>
      </div>
    </Card>
  );
}

function StepModulesTiming({ blueprint, setBP, setModule, activeModuleIdx, setActiveModuleIdx, readOnly }) {
  const addModule = () => {
    const section = blueprint.section || 'math';
    const nextCode = `m${(blueprint.modules?.length || 0) + 1}`;
    const nm = emptyModule(nextCode, section);
    setBP({ modules: [...(blueprint.modules || []), nm] });
  };
  const removeModule = (idx) => {
    if ((blueprint.modules || []).length <= 1) { toast.error('Need at least 1 module'); return; }
    setBP({ modules: blueprint.modules.filter((_, i) => i !== idx) });
    setActiveModuleIdx(0);
  };

  return (
    <Card className="p-4 space-y-3" data-testid="tb-step-modules">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-gray-900">Modules</div>
          <div className="text-[11px] text-gray-500">Break time between modules is inherited from the exam config (10 min for DSAT).</div>
        </div>
        {!readOnly && blueprint.test_type !== 'full_mock' && (
          <Button size="sm" variant="outline" onClick={addModule} data-testid="tb-add-module">
            <Plus className="h-4 w-4 mr-1" /> Add module
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(blueprint.modules || []).map((m, idx) => (
          <div key={idx} className={`border rounded p-3 ${activeModuleIdx === idx ? 'border-red-700 bg-red-50/40' : 'border-gray-200'}`} data-testid={`tb-module-${idx}`}>
            <div className="flex items-center justify-between mb-2">
              <button
                className="text-xs font-bold text-gray-900"
                onClick={() => setActiveModuleIdx(idx)}
              >
                Module {idx + 1} · {m.label || m.code}
              </button>
              {!readOnly && blueprint.test_type !== 'full_mock' && (
                <button className="text-gray-400 hover:text-red-600" onClick={() => removeModule(idx)} title="Remove module" data-testid={`tb-remove-module-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Label</Label>
                <Input value={m.label || ''} onChange={(e) => setModule(idx, { label: e.target.value })} disabled={readOnly} data-testid={`tb-mod-label-${idx}`} />
              </div>
              <div>
                <Label className="text-[10px]">Section</Label>
                <select
                  className="border rounded px-2 py-1.5 text-sm w-full h-9"
                  value={m.section || 'math'}
                  onChange={(e) => setModule(idx, { section: e.target.value, filters: { ...(m.filters || {}), section: e.target.value } })}
                  disabled={readOnly}
                >
                  <option value="math">Math</option>
                  <option value="rw">R&amp;W</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px]">Minutes</Label>
                <Input type="number" min={1} value={m.minutes || 0}
                  onChange={(e) => setModule(idx, { minutes: parseInt(e.target.value || '0', 10) })} disabled={readOnly}
                  data-testid={`tb-mod-min-${idx}`} />
              </div>
              <div>
                <Label className="text-[10px]">Question count</Label>
                <Input type="number" min={1} value={m.question_count || 0}
                  onChange={(e) => setModule(idx, { question_count: parseInt(e.target.value || '0', 10) })} disabled={readOnly}
                  data-testid={`tb-mod-qc-${idx}`} />
              </div>
              <div>
                <Label className="text-[10px]">Selection mode</Label>
                <select
                  className="border rounded px-2 py-1.5 text-sm w-full h-9"
                  value={m.selection_mode || 'manual'}
                  onChange={(e) => setModule(idx, { selection_mode: e.target.value })}
                  disabled={readOnly}
                  data-testid={`tb-mod-mode-${idx}`}
                >
                  <option value="manual">Manual pick</option>
                  <option value="random">Random from pool</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="text-[10px] flex items-center gap-1 mt-4">
                  <input type="checkbox" checked={!!m.calculator}
                    onChange={(e) => setModule(idx, { calculator: e.target.checked })} disabled={readOnly} />
                  Calculator
                </label>
              </div>
            </div>
            <div className="mt-2 text-[11px] text-gray-600">
              {m.selection_mode === 'manual'
                ? `${(m.question_ids || []).length} question(s) selected`
                : `Random draw of ${m.question_count} from filters`}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function StepQuestionSelection({ blueprint, setModule, setModuleFilter, activeModuleIdx, setActiveModuleIdx, readOnly }) {
  const modules = blueprint.modules || [];
  const active = modules[activeModuleIdx] || modules[0];
  if (!active) return <Card className="p-4 text-sm text-gray-500">Add a module first.</Card>;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 flex-wrap" data-testid="tb-mod-tabs">
        {modules.map((m, i) => (
          <button key={i} onClick={() => setActiveModuleIdx(i)}
            className={`text-[11px] px-3 py-1.5 rounded-full border ${i === activeModuleIdx ? 'bg-red-700 text-white border-red-700' : 'bg-white text-gray-700 border-gray-300'}`}
            data-testid={`tb-mod-tab-${i}`}>
            {m.label || m.code} · {(m.question_ids || []).length} / {m.question_count || 0}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,400px),1fr] gap-3">
        {/* LEFT — filters + results */}
        <QuestionBrowser
          module={active}
          moduleIdx={activeModuleIdx}
          setModuleFilter={setModuleFilter}
          selectedIds={active.question_ids || []}
          onAdd={(qid) => {
            if (readOnly) return;
            const cur = active.question_ids || [];
            if (cur.includes(qid)) return;
            // De-dup across all modules
            for (const m of modules) if (m !== active && (m.question_ids || []).includes(qid)) {
              toast.error('This question is already in another module of this test.'); return;
            }
            setModule(activeModuleIdx, { question_ids: [...cur, qid] });
          }}
          onRemove={(qid) => {
            if (readOnly) return;
            setModule(activeModuleIdx, { question_ids: (active.question_ids || []).filter((x) => x !== qid) });
          }}
          readOnly={readOnly}
        />

        {/* RIGHT — selected list */}
        <SelectedList
          module={active}
          onReorder={(newIds) => setModule(activeModuleIdx, { question_ids: newIds })}
          onRemove={(qid) => setModule(activeModuleIdx, { question_ids: (active.question_ids || []).filter((x) => x !== qid) })}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
}

function QuestionBrowser({ module, moduleIdx, setModuleFilter, selectedIds, onAdd, readOnly }) {
  const f = module.filters || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('exam_type', 'dsat');
      params.set('status', 'published');
      params.set('limit', '50');
      if (f.section || module.section) params.set('section', f.section || module.section);
      if (f.skill) params.set('skill', f.skill);
      if (f.difficulty) params.set('difficulty', f.difficulty);
      if (q) params.set('q', q);
      const r = await ax.get(`/api/exam/v2/questions?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [f.section, f.skill, f.difficulty, module.section, q]);

  useEffect(() => { load(); }, [load]);

  const setF = (patch) => setModuleFilter(moduleIdx, patch);

  return (
    <Card className="p-3 space-y-2" data-testid="tb-browser">
      <div className="text-xs font-bold text-gray-800">Question Bank filters</div>
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prompt / passage / tag"
            className="pl-7 h-9 text-xs" data-testid="tb-browser-search" />
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="tb-browser-refresh">
          <RefreshCcw className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px]">Section</Label>
          <select className="border rounded px-2 py-1 text-xs w-full h-8"
            value={f.section || ''} onChange={(e) => setF({ section: e.target.value })}
            data-testid="tb-browser-section">
            <option value="">Any</option>
            <option value="math">Math</option>
            <option value="rw">R&amp;W</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Difficulty</Label>
          <select className="border rounded px-2 py-1 text-xs w-full h-8"
            value={f.difficulty || ''} onChange={(e) => setF({ difficulty: e.target.value })}
            data-testid="tb-browser-difficulty">
            <option value="">Any</option>
            {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Skill</Label>
          <Input value={f.skill || ''} onChange={(e) => setF({ skill: e.target.value })}
            placeholder="e.g. linear equations" className="h-8 text-xs"
            data-testid="tb-browser-skill" />
        </div>
        <div>
          <Label className="text-[10px]">Domain</Label>
          <Input value={f.domain || ''} onChange={(e) => setF({ domain: e.target.value })}
            placeholder="e.g. Algebra" className="h-8 text-xs"
            data-testid="tb-browser-domain" />
        </div>
        <div>
          <Label className="text-[10px]">Question type</Label>
          <select className="border rounded px-2 py-1 text-xs w-full h-8"
            value={f.question_type || ''} onChange={(e) => setF({ question_type: e.target.value })}>
            <option value="">Any</option>
            {QTYPES.map((qt) => <option key={qt} value={qt}>{qt.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Sub-skill</Label>
          <Input value={f.subskill || ''} onChange={(e) => setF({ subskill: e.target.value })} className="h-8 text-xs" placeholder="(free text)" />
        </div>
      </div>
      <div className="text-[10px] text-gray-500">{loading ? 'Loading…' : `${items.length} of ${total} match`}</div>
      <div className="max-h-[520px] overflow-y-auto border rounded" data-testid="tb-browser-results">
        {items.map((qi) => {
          const already = selectedIds.includes(qi.question_id);
          return (
            <div key={qi.question_id} className="px-2 py-2 border-b last:border-0 flex items-start gap-2 hover:bg-gray-50" data-testid={`tb-result-${qi.question_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                  {qi.section} · {qi.skill || qi.domain || '—'} · {qi.difficulty}
                </div>
                <div className="text-xs text-gray-900 truncate">{qi.prompt}</div>
              </div>
              <Button size="sm" variant={already ? 'outline' : 'default'}
                onClick={() => onAdd(qi.question_id)} disabled={already || readOnly}
                className={already ? '' : 'bg-red-700 hover:bg-red-800 text-white'}
                data-testid={`tb-add-${qi.question_id}`}>
                {already ? 'Added' : <><Plus className="h-3.5 w-3.5" /></>}
              </Button>
            </div>
          );
        })}
        {!loading && items.length === 0 && (
          <div className="p-6 text-center text-xs text-gray-500">No matching questions.</div>
        )}
      </div>
    </Card>
  );
}

function SelectedList({ module, onReorder, onRemove, readOnly }) {
  const ids = module.question_ids || [];
  const [items, setItems] = useState([]);
  const [estimate, setEstimate] = useState({ estimated_minutes: 0, count: 0, by_difficulty: {} });
  const [loading, setLoading] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!ids.length) { setItems([]); setEstimate({ estimated_minutes: 0, count: 0, by_difficulty: {} }); return; }
    setLoading(true);
    try {
      const results = [];
      // batch of 100
      const chunk = ids.slice(0, 100);
      const r = await ax.get(`/api/exam/v2/questions?limit=1000&exam_type=dsat&status=published`);
      const map = new Map((r.data?.items || []).map((x) => [x.question_id, x]));
      // Also fetch any not covered via status = draft/review just in case
      for (const qid of chunk) {
        if (map.has(qid)) results.push(map.get(qid));
      }
      // second fetch for missing (rare — questions in review/draft)
      const missing = chunk.filter((x) => !map.has(x));
      if (missing.length) {
        const r2 = await ax.get(`/api/exam/v2/questions?limit=200&exam_type=dsat&status=`);
        for (const it of (r2.data?.items || [])) if (missing.includes(it.question_id)) results.push(it);
      }
      // preserve order
      const byId = new Map(results.map((x) => [x.question_id, x]));
      setItems(ids.map((qid) => byId.get(qid)).filter(Boolean));

      // estimate
      const est = await ax.post('/api/exam/v2/tests/estimate', { question_ids: ids });
      setEstimate(est.data || {});
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [ids]);

  useEffect(() => { fetchDetails(); }, [fetchDetails]);

  const move = (idx, delta) => {
    if (readOnly) return;
    const to = idx + delta;
    if (to < 0 || to >= ids.length) return;
    const next = [...ids];
    const [it] = next.splice(idx, 1);
    next.splice(to, 0, it);
    onReorder(next);
  };

  // HTML5 drag-and-drop reorder
  const dragIdxRef = useRef(null);
  const onDragStart = (idx) => (e) => { dragIdxRef.current = idx; e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetIdx) => (e) => {
    e.preventDefault();
    const src = dragIdxRef.current;
    if (src == null || src === targetIdx || readOnly) return;
    const next = [...ids];
    const [it] = next.splice(src, 1);
    next.splice(targetIdx, 0, it);
    onReorder(next);
    dragIdxRef.current = null;
  };

  return (
    <Card className="p-3 space-y-2" data-testid="tb-selected">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold text-gray-800">Selected questions ({ids.length})</div>
        <div className="flex items-center gap-3 text-[11px] text-gray-600">
          <span className="inline-flex items-center gap-1"><ListChecks className="h-3 w-3" /> {estimate.count || ids.length} q</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {estimate.estimated_minutes || 0} min</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 text-[10px]">
        {Object.entries(estimate.by_difficulty || {}).map(([k, v]) => (
          <Badge key={k} className="bg-gray-100 text-gray-700">{k}: {v}</Badge>
        ))}
      </div>
      <div className="max-h-[520px] overflow-y-auto border rounded" data-testid="tb-selected-list">
        {items.length === 0 && !loading && (
          <div className="p-6 text-center text-xs text-gray-500">No questions selected yet — pick from the left panel.</div>
        )}
        {loading && <div className="p-4 text-center text-[11px] text-gray-500"><Loader2 className="h-3 w-3 inline animate-spin mr-1" /> Refreshing…</div>}
        {items.map((qi, idx) => (
          <div
            key={qi.question_id}
            className="px-2 py-2 border-b last:border-0 flex items-start gap-2 hover:bg-gray-50 cursor-move"
            draggable={!readOnly}
            onDragStart={onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={onDrop(idx)}
            data-testid={`tb-selected-${qi.question_id}`}
          >
            <div className="text-[10px] text-gray-400 w-5 shrink-0 mt-0.5">{idx + 1}.</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                {qi.section} · {qi.skill || qi.domain || '—'} · {qi.difficulty}
              </div>
              <div className="text-xs text-gray-900 truncate">{qi.prompt}</div>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-0.5">
                <button className="p-1 text-gray-400 hover:text-gray-900" onClick={() => move(idx, -1)} title="Move up">
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button className="p-1 text-gray-400 hover:text-gray-900" onClick={() => move(idx, 1)} title="Move down">
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button className="p-1 text-gray-400 hover:text-red-600" onClick={() => onRemove(qi.question_id)} title="Remove"
                  data-testid={`tb-remove-selected-${qi.question_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function StepReview({ blueprint, existing }) {
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => {
    if (!existing?.test_id) return;
    ax.get(`/api/exam/v2/tests/${existing.test_id}/analytics`).then((r) => setAnalytics(r.data)).catch(() => {});
  }, [existing?.test_id]);

  const summary = useMemo(() => {
    const modules = blueprint.modules || [];
    const totalQ = modules.reduce((sum, m) => sum + ((m.question_ids?.length) || (m.question_count || 0)), 0);
    const totalMin = modules.reduce((sum, m) => sum + (m.minutes || 0), 0);
    return { totalQ, totalMin, modules };
  }, [blueprint]);

  return (
    <Card className="p-4 space-y-3" data-testid="tb-step-review">
      <div className="text-sm font-bold text-gray-900">Review</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <SummaryCell label="Type" value={blueprint.test_type} />
        <SummaryCell label="Section" value={blueprint.section || 'Both'} />
        <SummaryCell label="Modules" value={summary.modules.length} />
        <SummaryCell label="Total questions" value={summary.totalQ} />
        <SummaryCell label="Est. duration" value={`${summary.totalMin} min`} />
        <SummaryCell label="Status" value={blueprint.status} />
        <SummaryCell label="Version" value={`v${blueprint.version || 1}`} />
        <SummaryCell label="Untimed allowed" value={blueprint.allow_untimed ? 'Yes' : 'No'} />
      </div>

      <div className="border rounded p-3">
        <div className="text-xs font-bold text-gray-800 mb-2">Modules</div>
        <div className="space-y-2">
          {(blueprint.modules || []).map((m, i) => (
            <div key={i} className="border-b last:border-0 pb-2">
              <div className="text-xs font-bold text-gray-900">{m.label || m.code}</div>
              <div className="text-[11px] text-gray-600">
                Section: <b className="uppercase">{m.section}</b> · Time: {m.minutes} min · Questions: {(m.question_ids || []).length || m.question_count} · Mode: {m.selection_mode}
                {m.calculator ? ' · Calculator' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>

      {existing?.test_id && (
        <div className="border rounded p-3">
          <div className="text-xs font-bold text-gray-800 mb-1">Live analytics (this test)</div>
          {analytics ? (
            <div className="text-[11px] text-gray-700">
              Attempts: {analytics.attempts} · Avg score: {analytics.avg_score_pct ?? '—'}% · Avg completion: {analytics.avg_completion_seconds ? `${Math.round(analytics.avg_completion_seconds / 60)} min` : '—'}
            </div>
          ) : <div className="text-[11px] text-gray-500">No attempts yet.</div>}
        </div>
      )}
    </Card>
  );
}

function SummaryCell({ label, value }) {
  return (
    <div className="p-2 border rounded bg-gray-50">
      <div className="text-[9px] text-gray-500 uppercase tracking-wider font-bold">{label}</div>
      <div className="text-sm text-gray-900 font-semibold capitalize">{String(value ?? '—').replace('_', ' ')}</div>
    </div>
  );
}
